/**
 * Foundation/Core — pannello "Informazioni di sistema" (solo amministratori).
 * Riusabile da ogni modulo Suite: dati da `GET /api/system/runtime`.
 */
import React, { useEffect, useState } from "react";
import { Copy, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/foundation/api/client";
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { buildSystemInfoRows, formatSystemInfoText, getClientContext } from "./systemInfo";

export function SystemInfoDialog({ open, onOpenChange }) {
  const [rows, setRows] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setRows(null);
    setError(null);
    api.get("/system/runtime")
      .then(({ data }) => alive && setRows(buildSystemInfoRows(data, getClientContext())))
      .catch((e) => alive && setError(e?.response?.status === 403 ? "Accesso riservato agli amministratori." : "Informazioni non disponibili."));
    return () => { alive = false; };
  }, [open]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(formatSystemInfoText(rows));
      toast.success("Informazioni tecniche copiate negli appunti");
    } catch (_) {
      toast.error("Copia non riuscita");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="system-info-dialog">
        <DialogHeader>
          <DialogTitle>Informazioni di sistema</DialogTitle>
          <DialogDescription>Stato tecnico del software, da allegare alle richieste di assistenza.</DialogDescription>
        </DialogHeader>
        {!rows && !error && (
          <div className="flex items-center gap-2 py-6 text-sm text-suite-neutral-500" data-testid="system-info-loading">
            <Loader2 className="h-4 w-4 animate-spin" /> Caricamento…
          </div>
        )}
        {error && <p className="py-4 text-sm text-red-600" data-testid="system-info-error">{error}</p>}
        {rows && (
          <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 text-xs" data-testid="system-info-list">
            {rows.map((r) => (
              <React.Fragment key={r.id}>
                <dt className="text-suite-neutral-500">{r.label}</dt>
                <dd className="break-all font-mono text-suite-neutral-800" data-testid={`system-info-${r.id}`}>{r.value ?? "—"}</dd>
              </React.Fragment>
            ))}
          </dl>
        )}
        <div className="flex justify-end pt-2">
          <Button onClick={copy} disabled={!rows} size="sm" data-testid="system-info-copy-btn">
            <Copy className="mr-2 h-4 w-4" /> Copia informazioni tecniche
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default SystemInfoDialog;
